import React from 'react';

function formatTime(totalSeconds) {
  if (!totalSeconds || totalSeconds < 0) return '0:00';
  const mins = Math.floor(totalSeconds / 60);
  const secs = totalSeconds % 60;
  return `${mins}:${String(secs).padStart(2, '0')}`;
}

const HEADER_CLS = 'text-[9px] uppercase tracking-wider text-gray-400 leading-none';
const VALUE_CLS = 'text-xs font-mono tabular-nums leading-none';
const CHARGE_CLS = 'text-[11px] font-mono font-bold tabular-nums leading-none';

const MeterDisplay = ({
  fare,
  fareMode,
  distance,
  isRunning,
  /** Seconds accumulated in time-billing mode (block time fare uses this, not trip wall). */
  fareTimeSeconds = 0,
  waitTime,
  waitCharge = 0,
  distanceCharge = 0,
  timeCharge = 0,
  flagRate = 0,
}) => {
  if (!isRunning) return null;

  const modeLabel = fareMode === 'time' ? 'TIME' : 'DIST';
  const modeColor = fareMode === 'time' ? 'bg-amber-500' : 'bg-green-600';
  const d = Number(distance) || 0;
  const distLabel = d > 0 && d < 0.1 ? d.toFixed(2) : d.toFixed(1);
  const dCharge = Number(distanceCharge) || 0;
  const tCharge = Number(timeCharge) || 0;
  const wCharge = Number(waitCharge) || 0;
  const fCharge = Number(flagRate) || 0;

  // 5 columns x 3 rows. Row 1 = headers (aligned across all cols).
  // Row 2 = primary value. Row 3 = accumulated charge ("+$X.XX").
  // FARE value spans rows 2-3 (large dollar). MODE badge sits in row 2 with the
  // flag/drop charge below it in row 3.
  return (
    <div className="grid grid-cols-5 gap-x-3 gap-y-1 bg-gray-900 text-white rounded-lg px-3 py-1.5 shadow-lg w-full text-center items-center">
      {/* Row 1: headers */}
      <span className={HEADER_CLS}>Fare</span>
      <span className={HEADER_CLS}>Mode</span>
      <span className={HEADER_CLS}>Dist</span>
      <span className={HEADER_CLS}>Time</span>
      <span className={HEADER_CLS}>Wait</span>

      {/* FARE value spans rows 2-3 */}
      <span className="row-span-2 text-lg font-mono font-bold tabular-nums flex items-center justify-center">
        ${(fare ?? 0).toFixed(2)}
      </span>

      {/* MODE badge in row 2 — narrow, compact */}
      <span className="flex items-center justify-center">
        <span className={`text-[8px] font-bold px-1 py-px rounded ${modeColor}`}>
          {modeLabel}
        </span>
      </span>

      {/* DIST value (row 2) */}
      <span className={VALUE_CLS}>{distLabel} mi</span>

      {/* TIME value (row 2) — fare-billing seconds so charges trace to FARE total */}
      <span className={VALUE_CLS}>{formatTime(fareTimeSeconds || 0)}</span>

      {/* WAIT value (row 2) */}
      <span
        className={`${VALUE_CLS} ${(waitTime || 0) > 0 ? 'text-amber-400' : 'text-gray-500'}`}
      >
        {formatTime(waitTime || 0)}
      </span>

      {/* Row 3 charges — bright when accruing, dim when 0. MODE col shows flag/drop rate. */}
      {/* FARE col row 3 absorbed by row-span-2 above */}
      <span className={`${CHARGE_CLS} ${fCharge > 0 ? 'text-sky-300' : 'text-gray-600'}`}>
        +${fCharge.toFixed(2)}
      </span>
      <span className={`${CHARGE_CLS} ${dCharge > 0 ? 'text-emerald-300' : 'text-gray-600'}`}>
        +${dCharge.toFixed(2)}
      </span>
      <span className={`${CHARGE_CLS} ${tCharge > 0 ? 'text-yellow-300' : 'text-gray-600'}`}>
        +${tCharge.toFixed(2)}
      </span>
      <span className={`${CHARGE_CLS} ${wCharge > 0 ? 'text-yellow-300' : 'text-gray-600'}`}>
        +${wCharge.toFixed(2)}
      </span>
    </div>
  );
};

export default MeterDisplay;
